import { io, Socket } from 'socket.io-client';
import { getAuthToken } from './api';

/**
 * Same-host API origin for builds where VITE_API_URL was not supplied:
 * app.example.com -> https://api.example.com, localhost -> :5000.
 */
const deriveApiOrigin = (): string => {
    const { protocol, hostname } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return 'http://localhost:5000';
    }
    return `${protocol}//api.${hostname.replace(/^www\.|^app\./, '')}`;
};

class SocketService {
    private static instance: SocketService;
    private socket: Socket | null = null;
    private listeners: Map<string, Function[]> = new Map();

    private constructor() { }

    public static getInstance(): SocketService {
        if (!SocketService.instance) {
            SocketService.instance = new SocketService();
        }
        return SocketService.instance;
    }

    public connect(): void {
        if (this.socket?.connected) return;

        const token = getAuthToken() || '';
        // Never fall back to a hardcoded host: on a dedicated agency
        // installation that would open an authenticated socket against a
        // different customer's backend. Derive api.<domain> from the page
        // instead, matching getImageUrl() in utils/helpers.ts.
        const url = import.meta.env.VITE_API_URL || deriveApiOrigin();

        this.socket = io(url, {
            auth: { token },
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: 10,
            reconnectionDelay: 500,
            reconnectionDelayMax: 5000,
            randomizationFactor: 0.5,
        });

        this.socket.on('connect', () => {
            const agency = localStorage.getItem('agency');
            const agencyId = agency ? JSON.parse(agency)._id || JSON.parse(agency).id : undefined;
            if (agencyId) {
                this.emit('join-agency', agencyId);
            }
        });

        this.socket.on('disconnect', () => {
            console.log('Socket disconnected');
        });

        // Re-attach listeners
        this.listeners.forEach((callbacks, event) => {
            callbacks.forEach(callback => {
                this.socket?.on(event, callback as any);
            });
        });
    }

    public disconnect(): void {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
        }
    }

    public on(event: string, callback: Function): void {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, []);
        }
        this.listeners.get(event)?.push(callback);

        if (this.socket) {
            this.socket.on(event, callback as any);
        }
    }

    public off(event: string, callback?: Function): void {
        if (callback) {
            const callbacks = this.listeners.get(event);
            if (callbacks) {
                const index = callbacks.indexOf(callback);
                if (index !== -1) {
                    callbacks.splice(index, 1);
                }
            }
            this.socket?.off(event, callback as any);
        } else {
            this.listeners.delete(event);
            this.socket?.off(event);
        }
    }

    public emit(event: string, data: any): void {
        if (this.socket && this.socket.connected) {
            this.socket.emit(event, data);
        } else {
            console.warn('Socket not connected, cannot emit event:', event);
        }
    }

    public isConnected(): boolean {
        return !!this.socket?.connected;
    }

    public getSocket(): Socket | null {
        return this.socket;
    }
}

export default SocketService.getInstance();
